import type { ExportInfo, GridIncomplete } from "../types/grid.ts";
import { BuildStates } from "../types/grid.ts";

/**
 * Data that arrived incomplete because the database failed part-way through sending it.
 *
 * A streamed grid response cannot turn into an error once its rows have started, so it ends with
 * the rows it has and says so in `incomplete`; `parseGridData` reports that here, whichever
 * component asked for the data, and `IncompleteDataAlert` (mounted once in app.tsx) shows it as a
 * popup the user has to acknowledge. Nothing that reads a grid has to remember to check.
 */

type Listener = (info: GridIncomplete) => void;
let listener: Listener | null = null;

/** Registers the one place that shows the warning. Returns the unregister function. */
export function onIncompleteGrid(l: Listener): () => void {
  listener = l;
  return () => {
    if (listener === l) listener = null;
  };
}

/** Called by parseGridData for a response that carries `incomplete`. */
export function reportIncompleteGrid(info: GridIncomplete): void {
  if (listener) listener(info);
  else console.error("Grid data is incomplete, and nothing is registered to show it:", info);
}

/** The popup text for an incomplete grid response. */
export function incompleteGridMessage(info: GridIncomplete): string {
  const rows = info.rowsRead.toLocaleString();
  return info.rowsComplete
    ? `All ${rows} rows loaded, but a database error stopped what comes after them: the total row count and the footer totals are missing or wrong.`
    : `A database error stopped loading this data after ${rows} rows. The rows shown are correct, but rows are MISSING after them — counts, totals and anything worked out from this list do not cover all the data. Search again, or narrow the filters, before relying on it.`;
}

/** Done, but holding only the rows read before a database error. The server stores that as Done
 * with a negative row count (older clients only know Done); `Incomplete` says it outright. */
export function isIncompleteExport(info: ExportInfo): boolean {
  return info.BuildState === BuildStates.Done && (info.Incomplete === true || info.FileBuildStatus < 0);
}

/** Rows in an incomplete export file. */
export function incompleteExportRows(info: ExportInfo): number {
  return Math.abs(info.FileBuildStatus);
}

/** The popup text for an incomplete export, before it is downloaded. */
export function incompleteExportMessage(rows: number | null): string {
  const after = rows == null ? "" : ` after ${rows.toLocaleString()} rows`;
  return `A database error stopped this export${after}. The file holds only the rows read before that — it does NOT contain all the data. The file is marked INCOMPLETE in its name and inside it. Run the export again before relying on it.`;
}

/** An exported file the server marked incomplete — in its name and its processing result. */
export function isIncompleteExportFile(file: { OriginalFilename: string; Result: string | null }): boolean {
  return file.OriginalFilename.includes("(INCOMPLETE)") || (file.Result ?? "").startsWith("EXPORT INCOMPLETE");
}
